import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Box, Grid, LinearProgress, Typography } from '@material-ui/core';
import Card from 'src/components/Card';
import file_upload_rules from 'src/data/file-upload-rules';
import userImage from 'src/images/user.png';
import { MdAddAPhoto as AddPhotoIcon } from '@react-icons/all-files/md/MdAddAPhoto';
import { createUseStyles } from 'src/theming';
import Employee from 'src/interfaces/api/Employee';
import useAuth from 'src/hooks/api/useAuth';

interface Props {
  user: Employee;
  openToast: (success: boolean, message: string) => void;
}

const useStyles = createUseStyles({
  photoContainer: {
    cursor: 'pointer',
  },
  photoInfo: {
    opacity: 0,
    transition: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover': {
      opacity: 1,
    },
  },
});

const PhotoCard: FC<Props> = ({ user, openToast }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File>();
  const { fetch } = useAuth();

  const onUploadButtonClick = () => {
    inputRef.current?.click();
  };

  const toBase64 = (file): Promise<any> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result || '');
      reader.onerror = (error) => reject(error);
    });

  const postProfilePic = useCallback(
    async (file: File) => {
      const base64File = await toBase64(file);

      let data =
        'metadata=' +
        encodeURIComponent(
          JSON.stringify({
            profileImage: {
              typeOfFile: file.type.split('/')[1],
            },
          })
        );

      data += '&base64=' + encodeURIComponent(base64File.split('base64,')[1]);

      const response = await fetch('/api/addProfileImage', {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        method: 'POST',
        body: data,
      });

      const json = await response.json();

      if (json.isValid) {
        openToast(true, 'Photo de profil modifiée');
      } else {
        openToast(false, 'Erreur modification. Veuillez réessayer');
      }
    },
    [openToast]
  );

  const updateProfilePic = async (file: File) => {
    setLoading(true);

    try {
      await postProfilePic(file);
      setFile(file);
    } catch (e) {
      openToast(false, "Erreur lors de l'ajout du logo. Veuillez réessayer");
    }

    setLoading(false);
  };

  const handleChange = (e) => {
    e.preventDefault();
    let fileValid = true;

    if (e.target.files && e.target.files[0]) {
      const file: File = e.target.files[0];

      if (file.size > file_upload_rules.sizeLimit) {
        openToast(false, 'La taille maximale autorisée est de 3Mo');
        fileValid = false;
      } else if (file_upload_rules.acceptedImageTypes.indexOf(file.type) === -1) {
        openToast(false, 'Les formats autorisés sont jpeg et png');
        fileValid = false;
      }

      if (fileValid) {
        updateProfilePic(file);
      }
    }
  };

  const previewPhoto = useMemo(() => (file ? URL.createObjectURL(file) : user?.profileImage || userImage), [
    file,
    user,
  ]);

  return (
    <Card>
      <Grid justifyContent='center' container spacing={1}>
        <Grid item xs={12}>
          <Box fontWeight='bolder' fontSize='17px'>
            Photo de profil
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography variant='caption'>Changer la photo affichée sur votre page de profil.</Typography>
          <Box
            className={classes.photoContainer}
            position='relative'
            display='flex'
            justifyContent='center'
            alignItems='center'
            margin='auto'
            width='144px'
            height='144px'
            borderRadius='50%'
            overflow='hidden'
            mt='15px'
            border='1px dashed rgba(145, 158, 171, 0.32)'
            onClick={onUploadButtonClick}
          >
            <Box display='none' clone>
              <input type='file' id='input-file-upload' onChange={handleChange} ref={inputRef} />
            </Box>

            <Box
              zIndex={8}
              position='absolute'
              borderRadius='50%'
              width='calc(100% - 16px)'
              height='calc(100% - 16px)'
              overflow='hidden'
            >
              <Box height='100%' width='100%' clone>
                <img
                  style={{ objectFit: 'cover' }}
                  src={previewPhoto}
                  onError={({ currentTarget }) => {
                    currentTarget.src = userImage;
                  }}
                  alt='profil picture'
                />
              </Box>
            </Box>
            <Box
              display='flex'
              borderRadius='50%'
              position='absolute'
              alignItems='center'
              flexDirection='column'
              justifyContent='center'
              width='calc(100% - 16px)'
              height='calc(100% - 16px)'
              zIndex={9}
              color='rgb(255, 255, 255)'
              bgcolor='rgba(22, 28, 36, 0.64)'
              className={classes.photoInfo}
            >
              <AddPhotoIcon size={16} />
              <span>Remplacer</span>
            </Box>
          </Box>
        </Grid>

        {loading && (
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

export default PhotoCard;
